<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img v-if="currentRoutePermissions.add_purchaseorder"
             src="@/assets/images/common-icons/icon_create.svg" @click="createOrder"
             :title="$t('buttons.purchase_orders.create_order')">
        <img v-if="currentRoutePermissions.delete_purchaseorder"
             src="@/assets/images/common-icons/icon_delete.svg" @click="deleteOrder"
             :title="$t('buttons.purchase_orders.delete_order')">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.common.supplier')}}</label>
            <multiselect v-model="searchData.supplier" :max-height="200"
                         placeholder="" label="name" track-by="id"
                         :options="suppliers" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.status')}}</label>
            <multiselect v-model="searchData.status" :max-height="200"
                         placeholder=""
                         :options="status"
                         :taggable="true" :close-on-select="true"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.purchase_orders.PO_number')}}</label>
            <input class="form-control" v-model="searchData.order_nr" @keyup.enter="search"
                   placeholder="">
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="searchData.buyer" :max-height="200"
                         placeholder="" label="name" track-by="id"
                         :options="customers" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.creation_date')}}</label>
            <range-date-picker v-model="searchData.creation_date" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.creator')}}</label>
            <multiselect v-model="searchData.creator" :max-height="200"
                         placeholder="" label="username" track-by="id"
                         :options="users" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.contracts.contract_number')}}</label>
            <multiselect v-model="searchData.contracts" :max-height="200"
                         placeholder="" label="contract_nr" track-by="id"
                         :options="contractsList" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.delivery_date')}}</label>
            <range-date-picker v-model="searchData.delivery_term" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.transport')}}</label>
            <multiselect v-model="searchData.delivery_methods" :max-height="200"
                         placeholder="" label="name" track-by="id"
                         :options="delivery_methods" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <label>{{$t('labels.common.agent')}}</label>
            <multiselect v-model="searchData.agent" :max-height="200"
                         placeholder="" label="name" track-by="id"
                         :options="agents" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <input class="form-control"
                   v-model="searchData.quick_search"
                   placeholder=""
                   >
          </b-col>
          <b-col cols="4" offset="4" class="vertical-center-container d-flex justify-content-end">
            <label for="pagination-toggle" class="margin-right-5">{{$t('labels.common.pagination')}}</label>
            <input id="pagination-toggle" type="checkbox"
                   title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                   @change="togglePagination">
            <save-columns-btn :grid="gridOptions" :grid-name="gridName" class="margin-left-5"></save-columns-btn>
          </b-col>
        </b-row>
      </div>
    </div>
    <div>
      <b-row>
        <b-col>
          <ag-grid-vue class="ag-theme-balham grid" :gridOptions="gridOptions"
                       @filterChanged="updateTotalData"
                       :columnDefs="columns" :rowData="purchaseOrders" :pinnedBottomRowData="pinnedRowData"
                       @rowDoubleClicked="goToPurchaseOrder" :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-col>
      </b-row>
      <b-row class="pagination__wrapper" v-if="paginationState">
        <b-col cols="5">
          <div class="pagination-info">{{countOfViewPurchaseOrders}} {{$t('common_content.of')}} {{ordersCount}}
          </div>
        </b-col>
        <b-col cols="7">
          <b-pagination size="md"
                        :total-rows="ordersCount"
                        :per-page="searchObject.limit"
                        v-model="currentPaginateIndex"
                        @input="changePaginateState"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import {
    mapGetters,
    mapState
  } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import RangeDatePicker from 'vue2-datepicker';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import moment from 'moment';
  import {
    cleanFilters,
    saveFilters,
    setFilters,
    checkSavedFilterData,
    getArrayByProperty,
    setNotifyData
  } from '@/helpers/commonHelpers';
  import { gridMixins, updateTotalDataMixin } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins, updateTotalDataMixin],
    components: {
      Multiselect,
      RangeDatePicker,
      SaveColumnsBtn,
      'ag-grid-vue': AgGridVue,
    },
    data() {
      return {
        paginationState: true,
        currentPaginateIndex: 1,
        searchObject: {
          limit: 15,
          offset: 0
        },
        showCollapse: false,
        status: ['active', 'completed'],
        searchData: {
          supplier: [],
          contracts: [],
          buyer: [],
          status: '',
          creation_date: '',
          delivery_term: '',
          delivery_methods: [],
          order_nr: '',
          agent: [],
          creator: [],
          quick_search: ''
        }
      }
    },
    watch: {
      'searchData.quick_search': {
        deep: true,
        handler () {
          this.setQuickFilter()
        }
      }
    },
    methods: {
      async updateTotalData (event) {
        await this.updateTotalDataWith(event, async (params) => {
          await this.$store.dispatch('purchaseOrders/updatePurchaseOrdersTotalData', params);
        })
      },
      search() {
        this.currentPaginateIndex = 1;
        this.searchObject = {
          ...this.searchObject,
          ...this.searchData,
          creation_date_between_after: this.searchData.creation_date && this.searchData.creation_date[0] ? moment(this.searchData.creation_date[0]).format(DATE_FORMAT) : '',
          creation_date_between_before: this.searchData.creation_date && this.searchData.creation_date[1] ? moment(this.searchData.creation_date[1]).format(DATE_FORMAT) : '',
          delivery_term_between_after: this.searchData.delivery_term && this.searchData.delivery_term[0] ? moment(this.searchData.delivery_term[0]).format(DATE_FORMAT) : '',
          delivery_term_between_before: this.searchData.delivery_term && this.searchData.delivery_term[1] ? moment(this.searchData.delivery_term[1]).format(DATE_FORMAT) : '',
          contracts: getArrayByProperty(this.searchData.contracts, 'id').toString(),
          supplier: getArrayByProperty(this.searchData.supplier, 'id').toString(),
          buyer: getArrayByProperty(this.searchData.buyer, 'id').toString(),
          agent: getArrayByProperty(this.searchData.agent, 'id').toString(),
          creator: getArrayByProperty(this.searchData.creator, 'id').toString(),
          delivery_methods: getArrayByProperty(this.searchData.delivery_methods, 'id').toString()
        };
        this.$store.dispatch('purchaseOrders/loadPurchaseOrders', this.searchObject);
        saveFilters('purchaseOrders', this.searchData)
      },
      deleteOrder() {
        const id = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id');
        if (id.length) {
          this.$http.delete(`purchase/orders/`, {body: {id}}).then((response) => {
            this.$notify(setNotifyData('Success', response.body, 'success'));
            this.currentPaginateIndex = 1;
            this.$store.dispatch('purchaseOrders/loadPurchaseOrders', this.searchObject);
          }).catch((error) => {
            console.log(error);
            this.$notify(setNotifyData('', error.body, 'error'));
          });
        } else {
          this.$notify(setNotifyData('Select purchase orders for removing', '', 'warn'));
        }
      },
      createOrder() {
        this.$router.push({path: `/purchaseOrders/new`});
      },
      cleanFilters() {
        cleanFilters(this.searchData);
        this.searchData.status = 'active'
      },
      setQuickFilter() {
        this.gridOptions.api.setQuickFilter(this.searchData.quick_search);
      },
      changePaginateState() {
        const searchObject = {...this.searchObject, offset: (this.currentPaginateIndex - 1) * this.searchObject.limit};
        this.$store.dispatch('purchaseOrders/loadPurchaseOrders', searchObject);
      },
      goToPurchaseOrder(params) {
        if (params.data && params.data.id) {
          this.$router.push({path: `/purchaseOrders/${params.data.id}`})
        }
      },
      togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 15;
          this.searchObject.offset = 0;
        }
        this.search();
      },
      clearData() {
        this.$store.commit('purchaseOrders/setData', []);
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('woodware/getDeliveryMethods');
      this.$store.cache.dispatch('woodware/getUsers');
      this.$store.dispatch('partners/getSuppliers');
      this.$store.dispatch('partners/getCustomers');
      this.$store.dispatch('partners/getSaleAgents');
      this.$store.dispatch('contracts/loadContractsList');
      if (checkSavedFilterData('purchaseOrders')) {
        this.searchData = setFilters('purchaseOrders');
        this.search();
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    computed: {
      ...mapState('purchaseOrders', ['gridName']),
      ...mapState('generic', ['currentRoutePermissions']),
      ...mapState('woodware', ['delivery_methods', 'users']),
      ...mapState('partners', ['suppliers', 'customers', 'agents']),
      ...mapGetters('purchaseOrders', {
        gridOptions: 'gridOptions'
      }),
      ...mapState('purchaseOrders', ['pinnedRowData', 'ordersCount', 'purchaseOrders', 'columns']),
      ...mapState('contracts', ['contractsList']),
      countOfViewPurchaseOrders() {
        return this.purchaseOrders.length;
      }
    }
  }
</script>

<style scoped>

  .grid {
    height: calc(100vh - 460px);
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/purchases/orders/PurchaseOrdersList.vue